import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import type {
  AccountSecret,
  CreateAccountSecretRequest,
  ListAccountSecretsResponse,
  UpdateAccountSecretRequest,
} from '@localagent/sdk';

const SECRETS_KEY = ['account-secrets'] as const;

function setSecretList(
  queryClient: QueryClient,
  update: (prev: AccountSecret[]) => AccountSecret[],
): void {
  queryClient.setQueryData<ListAccountSecretsResponse>(SECRETS_KEY, (prev) => {
    const secrets = update(prev?.secrets ?? []).sort((a, b) => a.name.localeCompare(b.name));
    return { secrets, count: secrets.length };
  });
}

export function useAccountSecrets() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: SECRETS_KEY,
    queryFn: () => getSDK().secrets.list(),
    enabled: isAuthenticated && sdkConfigured,
  });
}

export function useCreateAccountSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateAccountSecretRequest) => getSDK().secrets.create(req),
    onSuccess: (row) => {
      setSecretList(queryClient, (prev) => [...prev.filter((s) => s.id !== row.id), row]);
    },
  });
}

export function useUpdateAccountSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ secretId, req }: { secretId: string; req: UpdateAccountSecretRequest }) =>
      getSDK().secrets.update(secretId, req),
    onSuccess: (row) => {
      setSecretList(queryClient, (prev) => [...prev.filter((s) => s.id !== row.id), row]);
    },
  });
}

export function useDeleteAccountSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (secretId: string) => getSDK().secrets.delete(secretId),
    onSuccess: (_res, secretId) => {
      setSecretList(queryClient, (prev) => prev.filter((s) => s.id !== secretId));
    },
  });
}

/**
 * Reveal is imperative and deliberately not a useQuery: caching the plaintext
 * would defeat on-demand reveal. Callers hold the value in component state and
 * drop it on unmount.
 */
export async function revealAccountSecret(secretId: string): Promise<string> {
  const res = await getSDK().secrets.reveal(secretId);
  return res.value;
}
